// Packages
import React from 'react';
import { gql } from '@apollo/client';
import { Switch, Typography, Box, Button } from '@mui/material';

// Styles
import styles from './PermissionsToggles.module.scss';

// Markup
import { Icon } from '../../../../blocks/icons';
import tooltipHelpers from '../../../../lib/helpers/tooltip';
import type { PermissionsTogglesPermissionsFragment } from '#types';

const permissionsTogglesPermissionsFragment = gql`
	fragment permissionsTogglesPermissions on ClientUser {
		plannerOrgHasConnectionPermissions
		plannerOrgCanSignContracts
		plannerOrgCanPayInvoices
		plannerOrgHasDocumentsPermissions
	}
`;

export const LABELS = {
	plannerOrgHasConnectionPermissions: 'Invite vendors + approve docs',
	plannerOrgCanSignContracts: 'Sign contracts on your behalf',
	plannerOrgCanPayInvoices: 'Pay invoices on your behalf ',
	plannerOrgHasDocumentsPermissions: 'Manage your folder',
} as const;

export const PERMISSIONS = [
	'plannerOrgHasConnectionPermissions',
	'plannerOrgCanSignContracts',
	'plannerOrgCanPayInvoices',
	'plannerOrgHasDocumentsPermissions',
] as const;

// Use the Apollo-generated fragment type for type safety
export type PermissionsSettings = Omit<
	PermissionsTogglesPermissionsFragment,
	'__typename'
>;

export type PermissionsTogglesProps = {
	permissions: PermissionsSettings | undefined;
	onChange: ( newPermissions: PermissionsSettings ) => void;
};

/**
 * Purely presentational "Toggles" component for the Permissions modal
 */
export const PermissionsToggles = ( {
	permissions,
	onChange,
}: PermissionsTogglesProps ): React.JSX.Element => {
	const getAllPermissionsObject = ( value = false ): PermissionsSettings =>
		PERMISSIONS.reduce( ( acc, key ) => ( { ...acc, [ key ]: value } ), {
			plannerOrgHasConnectionPermissions: false,
			plannerOrgCanSignContracts: false,
			plannerOrgCanPayInvoices: false,
			plannerOrgHasDocumentsPermissions: false,
		} );
	// in case we get undefined passed in
	const currentPermissions = permissions || getAllPermissionsObject();

	const anyItemsSelected = Object.values( currentPermissions ).some(
		( v ) => v === true
	);
	/**
	 * Special toggle rules for permissions in relation ot being a planner
	 */
	const onToggle = ( key: keyof PermissionsSettings, value: boolean ): void => {
		// if we're toggling off the "Planner" permissions, turn off all other permissions as well
		if ( key === 'plannerOrgHasConnectionPermissions' && value === false ) {
			onChange( getAllPermissionsObject() );
			/* otherwise, if "planner" permissions are "off" but the user wants to grant some other
       permission, enable "planner" permissions */
		} else if (
			key !== 'plannerOrgHasConnectionPermissions' &&
			value === true &&
			permissions &&
			permissions.plannerOrgHasConnectionPermissions === false
		) {
			onChange( {
				...permissions,
				plannerOrgHasConnectionPermissions: true,
				[ key ]: true,
			} );
		} else if ( permissions ) {
			onChange( { ...permissions, [ key ]: value } );
		}
	};
	return (
		<Box className={ styles.permissionsToggles }>
			<Typography variant='h6' className={ styles.labels }>
				Assign this vendor permissions to:
			</Typography>
			<Button
				variant='text'
				className={ [ styles.values, styles.unselectButton ].join( ' ' ) }
				onClick={ () => onChange( getAllPermissionsObject( !anyItemsSelected ) ) }
			>
				{ anyItemsSelected ? 'un' : '' }select all
			</Button>
			{ PERMISSIONS.map( ( key ) => (
				<React.Fragment key={ key }>
					<Box className={ styles.labels }>
						<Typography component='label'>{ LABELS[ key ] }</Typography>
						{ key === 'plannerOrgHasConnectionPermissions' && (
							<Icon
								className={ styles.icon }
								type='circle-questionmark'
								data-for='tooltip'
								data-tip={ tooltipHelpers.buildGenericToolTip(
									'This permission needs to be turned on for any other permission to be used.'
								) }
							/>
						) }
					</Box>
					<Box className={ styles.values }>
						<Typography component='span' className={ styles.switchLabel }>
							Off
						</Typography>
						<Switch
							checked={ currentPermissions[ key ] }
							onChange={ ( e ) => onToggle( key, e.target.checked ) }
							size='medium'
							slotProps={ {
								input: {
									'aria-label': LABELS[ key ],
									'data-cy': `permission-toggle-${ key }`,
								},
							} }
						/>
						<Typography component='span' className={ styles.switchLabel }>
							On
						</Typography>
					</Box>
				</React.Fragment>
			) ) }
		</Box>
	);
};

PermissionsToggles.fragments = {
	permissionsTogglesPermissionsFragment,
};
